import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  Card,
  CardContent,
  Grid,
  Divider,
  Avatar,
  Paper,
} from "@mui/material";
import { CloudUpload, Delete, CheckCircle, Business, Add, Download } from "@mui/icons-material";
import * as XLSX from "xlsx";

import CustomAutocomplete from "../../Components/CustomAutocomplete";
import CourseManagerDialog from "../../Components/CourseManagerDialog";

import HttpClient from "../../../../helpers/HttpClient";
import OverlayLoader from "../../../../components/OverlayLoader";
import { showToast } from "../../../../utils/toast";
import { validarNickname } from "../../../../utils/nicknameValidator";

interface Distribuidor {
  id: string;
  name: string;
}

interface UsuarioImportado {
  nombre_completo: string;
  documento: string;
  fecha_nacimiento: string;
  password: string;
  whatsapp: string;
  correo: string;
  nickname: string;
  cursos: string;
  cursos_chat: string;
}

interface ResultadoImportacion {
  usuarios: UsuarioImportado[];
  totalFilas: number;
  filasValidas: number;
  filasInvalidas: number;
  filasVacias: number;
  duplicados: { fila: number; tipo: string; valor: string }[];
  valido: boolean;
  errores: string[];
  erroresDetallados: ErrorDetallado[];
}

interface ErrorDetallado {
  fila: number;
  nombre?: string;
  documento?: string;
  correo?: string;
  fecha_nacimiento?: string;
  problemas: string[];
}

export default function UserImporterPage() {
  const [openLoader, setOpenLoader] = useState(true);
  const [distribuidoresDisponibles, setDistribuidoresDisponibles] = useState<Distribuidor[]>([]);
  const [cursosDisponibles, setCursosDisponibles] = useState<any[]>([]);
  const [distribuidorSeleccionado, setDistribuidorSeleccionado] = useState<Distribuidor | null>(null);
  
  const [archivoSeleccionado, setArchivoSeleccionado] = useState<File | null>(null);
  const [usuariosImportados, setUsuariosImportados] = useState<UsuarioImportado[]>([]);
  const [erroresImportacion, setErroresImportacion] = useState<string[]>([]);
  const [importacionValida, setImportacionValida] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [estadisticas, setEstadisticas] = useState({ filasValidas: 0, filasInvalidas: 0, filasVacias: 0, totalFilas: 0 });
  const [duplicados, setDuplicados] = useState<{ fila: number; tipo: string; valor: string }[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [jsonGenerado, setJsonGenerado] = useState<any>(null);
  const [paginaActual, setPaginaActual] = useState(0);
  const [numeroMeses, setNumeroMeses] = useState("12");
  const [numeroActivaciones, setNumeroActivaciones] = useState("1");
  const [erroresDetallados, setErroresDetallados] = useState<ErrorDetallado[]>([]);
  const filasPerPagina = 5;

  const validarCorreo = (correo: string): boolean => {
    const regexCorreo = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regexCorreo.test(correo);
  };

  const validarTelefonoInternacional = (phone: string): { valido: boolean; error?: string } => {
    if (!phone) {
      return { valido: false, error: "Teléfono vacío" };
    }
    // Validar formato internacional: debe empezar con + y tener entre 10-15 dígitos
    const phoneRegex = /^\+\d{10,15}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
      return { valido: false, error: "Teléfono inválido (ej: +57 3001234567)" };
    }
    return { valido: true };
  };


  const formatearFecha = (fecha: string): { valida: boolean; fecha: string } => {
    if (!fecha) return { valida: false, fecha: "" };


    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [year, month, day] = fecha.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (date instanceof Date && !isNaN(date.getTime())) {
        return { valida: true, fecha };
      }
    }


    if (!isNaN(Number(fecha))) {
      const excelDate = Number(fecha);

      const baseDate = new Date(1900, 0, 1);
      const date = new Date(baseDate.getTime() + (excelDate - 1) * 24 * 60 * 60 * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return { valida: true, fecha: `${year}-${month}-${day}` };
    }


    const regexDMA = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/;
    const matchDMA = fecha.match(regexDMA);
    if (matchDMA) {
      const [, day, month, year] = matchDMA;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (date instanceof Date && !isNaN(date.getTime())) {
        return {
          valida: true,
          fecha: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        };
      }
    }

    return { valida: false, fecha: "" };
  };

  const COLUMNAS_REQUERIDAS = [
    "nombre_completo",
    "documento",
    "fecha_nacimiento",
    "password",
    "whatsapp",
    "correo",
    "nickname",
    "cursos",
    "cursos_chat"
  ];
  const MAX_SIZE_MB = 10;
  const MAX_FILAS = 1000;


  const validarYParsearExcel = (file: File): Promise<ResultadoImportacion> => {
    return new Promise((resolve) => {
      const errores: string[] = [];
      const erroresDetallados: ErrorDetallado[] = [];
      const usuarios: UsuarioImportado[] = [];
      let filasVacias = 0;
      let filasInvalidas = 0;

      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_SIZE_MB) {
        errores.push(`El archivo excede el tamaño máximo de ${MAX_SIZE_MB}MB (tamaño actual: ${sizeMB.toFixed(2)}MB)`);
        resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
        return;
      }

      const nombreArchivo = file.name.toLowerCase();
      if (!nombreArchivo.endsWith('.xlsx') && !nombreArchivo.endsWith('.xls')) {
        errores.push('Solo se aceptan archivos Excel (.xlsx o .xls)');
        resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
        return;
      }

      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const data = e.target?.result as ArrayBuffer;
            const workbook = XLSX.read(data, { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];

            const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
            const headers: string[] = [];
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellAddress = XLSX.utils.encode_col(col) + '1';
              const cellValue = worksheet[cellAddress];
              headers.push(cellValue ? cellValue.v : '');
            }

            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as Record<string, unknown>[];

            if (jsonData.length === 0) {
              errores.push("El archivo está vacío. Por favor, agrega datos al Excel.");
              resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
              return;
            }

            if (jsonData.length > MAX_FILAS) {
              errores.push(`El archivo tiene más de ${MAX_FILAS} filas (${jsonData.length} encontradas)`);
              resolve({ usuarios: [], totalFilas: jsonData.length, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
              return;
            }

            const columnasEncontradas = Object.keys(jsonData[0]);
            const columnasValidas = COLUMNAS_REQUERIDAS.every(col => 
              columnasEncontradas.some(found => 
                found.trim().toLowerCase() === col.toLowerCase()
              )
            );

            if (!columnasValidas) {
              errores.push(
                `Columnas esperadas: ${COLUMNAS_REQUERIDAS.join(", ")}. ` +
                `Columnas encontradas: ${columnasEncontradas.join(", ")}`
              );
              resolve({ usuarios: [], totalFilas: jsonData.length, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
              return;
            }
            const correosVistos = new Set<string>();
            const documentosVistos = new Set<string>();
            const nicknamesVistos = new Set<string>();
            const duplicadosEncontrados: { fila: number; tipo: string; valor: string }[] = [];

            jsonData.forEach((fila, index: number) => {
              const erroresFila: string[] = [];
              const filaLimpia: Record<string, string> = {};
              
              COLUMNAS_REQUERIDAS.forEach(col => {
                filaLimpia[col] = "";
              });

              COLUMNAS_REQUERIDAS.forEach(col => {
                const clave = Object.keys(fila).find(k => k.trim().toLowerCase() === col.toLowerCase());
                if (clave) {
                  filaLimpia[col] = String(fila[clave] || "").trim();
                }
              });
              const filaVacia = COLUMNAS_REQUERIDAS.every(col => !filaLimpia[col]);
              
              if (filaVacia) {
                filasVacias++;
                return; // Saltar filas vacías sin contar como error
              }
              if (!filaLimpia["nombre_completo"]) {
                erroresFila.push("Nombre completo vacío");
              }
              if (!String(filaLimpia["documento"]).trim()) {
                erroresFila.push("Documento vacío");
              } else {
                const documentoStr = String(filaLimpia["documento"]).trim();
                if (documentosVistos.has(documentoStr)) {
                  duplicadosEncontrados.push({
                    fila: index + 2,
                    tipo: "documento",
                    valor: filaLimpia["documento"]
                  });
                  filasInvalidas++;
                  errores.push(`Fila ${index + 2}: Documento duplicado (${filaLimpia["documento"]})`);
                  erroresDetallados.push({
                    fila: index + 2,
                    nombre: filaLimpia["nombre_completo"] || "(vacío)",
                    documento: filaLimpia["documento"],
                    correo: filaLimpia["correo"] || "(vacío)",
                    fecha_nacimiento: filaLimpia["fecha_nacimiento"] || "(vacío)",
                    problemas: ["Documento duplicado"],
                  });
                  return;
                }
                documentosVistos.add(filaLimpia["documento"]);
              }
              if (!filaLimpia["fecha_nacimiento"]) {
                erroresFila.push("Fecha de nacimiento vacía");
              } else {
                const fechaFormato = formatearFecha(filaLimpia["fecha_nacimiento"]);
                if (!fechaFormato.valida) {
                  erroresFila.push("Fecha de nacimiento inválida (usa formato yyyy-mm-dd)");
                } else {
                  filaLimpia["fecha_nacimiento"] = fechaFormato.fecha;
                }
              }
              if (!filaLimpia["password"]) {
                erroresFila.push("Contraseña vacía");
              }
              if (!filaLimpia["whatsapp"]) {
                erroresFila.push("WhatsApp vacío");
              } else {
                const validacionTelefono = validarTelefonoInternacional(filaLimpia["whatsapp"]);
                if (!validacionTelefono.valido) {
                  erroresFila.push(validacionTelefono.error || "Teléfono inválido");
                }
              }
              if (!filaLimpia["correo"]) {
                erroresFila.push("Correo vacío");
              } else {
                if (!validarCorreo(filaLimpia["correo"])) {
                  erroresFila.push("Correo inválido (ej: usuario@ejemplo.com)");
                } else {
                  if (correosVistos.has(filaLimpia["correo"])) {
                    duplicadosEncontrados.push({
                      fila: index + 2,
                      tipo: "correo",
                      valor: filaLimpia["correo"]
                    });
                    filasInvalidas++;
                    errores.push(`Fila ${index + 2}: Correo duplicado (${filaLimpia["correo"]})`);
                    erroresDetallados.push({
                      fila: index + 2,
                      nombre: filaLimpia["nombre_completo"] || "(vacío)",
                      documento: filaLimpia["documento"] || "(vacío)",
                      correo: filaLimpia["correo"],
                      fecha_nacimiento: filaLimpia["fecha_nacimiento"] || "(vacío)",
                      problemas: ["Correo duplicado"],
                    });
                    return;
                  }
                  correosVistos.add(filaLimpia["correo"]);
                }
              }
              if (!String(filaLimpia["nickname"]).trim()) {
                erroresFila.push("Nombre de usuario vacío");
              } else {
                const nicknameStr = String(filaLimpia["nickname"]).trim();
                const validacionNickname = validarNickname(nicknameStr);
                if (!validacionNickname.valido) {
                  erroresFila.push(validacionNickname.error || "Nombre de usuario inválido");
                } else {
                  if (nicknamesVistos.has(nicknameStr)) {
                    duplicadosEncontrados.push({
                      fila: index + 2,
                      tipo: "nickname",
                      valor: nicknameStr
                    });
                    filasInvalidas++;
                    errores.push(`Fila ${index + 2}: Nombre de usuario duplicado (${nicknameStr})`);
                    erroresDetallados.push({
                      fila: index + 2,
                      nombre: filaLimpia["nombre_completo"] || "(vacío)",
                      documento: filaLimpia["documento"] || "(vacío)",
                      correo: filaLimpia["correo"] || "(vacío)",
                      fecha_nacimiento: filaLimpia["fecha_nacimiento"] || "(vacío)",
                      problemas: ["Nombre de usuario duplicado"],
                    });
                    return;
                  }
                  nicknamesVistos.add(nicknameStr);
                }
              }
              if (!filaLimpia["cursos"]) {
                erroresFila.push("Cursos vacíos");
              } else {
                const cursosString = filaLimpia["cursos"].trim();
                if (!cursosString.includes(",") && cursosString.length > 0) {
                  erroresFila.push("Cursos deben estar separados por comas (ej: ING1,EX19,WO19)");
                } else {
                  const cursosList = cursosString.split(",").map((c: string) => c.trim()).filter((c: string) => c.length > 0);
                  
                  if (cursosList.length === 0) {
                    erroresFila.push("Cursos inválidos - la lista no puede estar vacía");
                  } else {
                    const cursosDisponiblesCodes = (cursosDisponibles || []).map((c: any) => c.code);
                    const cursosInvalidos = cursosList.filter((curso: string) => !cursosDisponiblesCodes.includes(curso));
                    
                    if (cursosInvalidos.length > 0) {
                      erroresFila.push(`Cursos no válidos: ${cursosInvalidos.join(", ")}. Cursos disponibles: ${cursosDisponiblesCodes.join(", ")}`);
                    } else {
                      filaLimpia["cursos"] = cursosList.join(",");
                    }
                  }
                }
              }
              if (!filaLimpia["cursos_chat"]) {
                filaLimpia["cursos_chat"] = ""; // Vacío es válido para cursos_chat
              } else {
                const cursosChatString = filaLimpia["cursos_chat"].trim();
                
                if (cursosChatString.length > 0) {

                  const cursosChatList = cursosChatString.includes(",")
                    ? cursosChatString.split(",").map((c: string) => c.trim()).filter((c: string) => c.length > 0)
                    : [cursosChatString]; // Un solo código sin comas es válido
                  
                  if (cursosChatList.length > 0) {

                    const cursosUsuario = filaLimpia["cursos"].split(",").map((c: string) => c.trim());
                    

                    const cursosChatValidos = cursosChatList.filter((curso: string) => cursosUsuario.includes(curso));
                    

                    filaLimpia["cursos_chat"] = cursosChatValidos.join(",");
                  } else {
                    filaLimpia["cursos_chat"] = "";
                  }
                } else {
                  filaLimpia["cursos_chat"] = "";
                }
              }

              if (erroresFila.length > 0) {
                filasInvalidas++;
                errores.push(`Fila ${index + 2}: ${erroresFila.join("; ")}`);
                

                const errorDetallado: ErrorDetallado = {
                  fila: index + 2,
                  nombre: filaLimpia["nombre_completo"] || "(vacío)",
                  documento: filaLimpia["documento"] || "(vacío)",
                  correo: filaLimpia["correo"] || "(vacío)",
                  fecha_nacimiento: filaLimpia["fecha_nacimiento"] || "(vacío)",
                  problemas: erroresFila,
                };
                erroresDetallados.push(errorDetallado);
              } else {
                // Asegurar que whatsapp tenga + si existe
                let whatsapp = filaLimpia["whatsapp"];
                if (whatsapp && !whatsapp.startsWith('+')) {
                  whatsapp = '+' + whatsapp;
                }
                usuarios.push({
                  nombre_completo: String(filaLimpia["nombre_completo"] || "").trim(),
                  documento: String(filaLimpia["documento"] || "").trim(),
                  fecha_nacimiento: filaLimpia["fecha_nacimiento"],
                  password: filaLimpia["password"],
                  whatsapp: whatsapp,
                  correo: filaLimpia["correo"],
                  nickname: String(filaLimpia["nickname"] || "").trim(),
                  cursos: filaLimpia["cursos"],
                  cursos_chat: filaLimpia["cursos_chat"],
                });
              }
            });

            if (usuarios.length === 0) {
              errores.unshift("No hay filas válidas para importar. Verifica que los datos estén completos.");
              resolve({
                usuarios: [],
                totalFilas: jsonData.length,
                filasValidas: 0,
                filasInvalidas,
                filasVacias,
                duplicados: duplicadosEncontrados,
                valido: false,
                errores,
                erroresDetallados,
              });
              return;
            }

            resolve({
              usuarios,
              totalFilas: jsonData.length,
              filasValidas: usuarios.length,
              filasInvalidas,
              filasVacias,
              duplicados: duplicadosEncontrados,
              valido: true,
              errores,
              erroresDetallados,
            });
          } catch (error) {
            errores.push(`Error al procesar el archivo: ${(error as Error).message}`);
            resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
          }
        };

        reader.onerror = () => {
          errores.push("Error al leer el archivo");
          resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        errores.push(`Error al leer el archivo: ${(error as Error).message}`);
        resolve({ usuarios: [], totalFilas: 0, filasValidas: 0, filasInvalidas: 0, filasVacias: 0, duplicados: [], valido: false, errores, erroresDetallados: [] });
      }
    });
  };


  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProcesando(true);
    setArchivoSeleccionado(file);
    setErroresImportacion([]);
    setUsuariosImportados([]);
    setImportacionValida(false);
    setJsonGenerado(null);
    setDuplicados([]);
    setErroresDetallados([]);
    setPaginaActual(0);

    await new Promise(resolve => setTimeout(resolve, 500));

    const resultado = await validarYParsearExcel(file);
    
    setEstadisticas({
      filasValidas: resultado.filasValidas,
      filasInvalidas: resultado.filasInvalidas,
      filasVacias: resultado.filasVacias,
      totalFilas: resultado.totalFilas,
    });

    setDuplicados(resultado.duplicados);
    setErroresDetallados(resultado.erroresDetallados);

    if (resultado.valido) {
      setUsuariosImportados(resultado.usuarios);
      setImportacionValida(true);
      
      const json = {
        usuarios: resultado.usuarios,
        timestamp: new Date().toISOString(),
        totalUsuarios: resultado.usuarios.length,
      };
      setJsonGenerado(json);
      
    } else {
      setErroresImportacion(resultado.errores);
      showToast(`Error en la importación: ${resultado.errores[0]}`, "error");
    }


    if (resultado.erroresDetallados.length > 0 && resultado.errores.length > 0) {
      setErroresImportacion(resultado.errores);
    }
    
    setProcesando(false);
  };


  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    setProcesando(true);
    setArchivoSeleccionado(file);
    setErroresImportacion([]);
    setUsuariosImportados([]);
    setImportacionValida(false);
    setJsonGenerado(null);
    setDuplicados([]);
    setErroresDetallados([]);
    setPaginaActual(0);

    await new Promise(resolve => setTimeout(resolve, 500));

    const resultado = await validarYParsearExcel(file);
    
    setEstadisticas({
      filasValidas: resultado.filasValidas,
      filasInvalidas: resultado.filasInvalidas,
      filasVacias: resultado.filasVacias,
      totalFilas: resultado.totalFilas,
    });

    setDuplicados(resultado.duplicados);
    setErroresDetallados(resultado.erroresDetallados);

    if (resultado.valido) {
      setUsuariosImportados(resultado.usuarios);
      setImportacionValida(true);
      
      const json = {
        usuarios: resultado.usuarios,
        timestamp: new Date().toISOString(),
        totalUsuarios: resultado.usuarios.length,
      };
      setJsonGenerado(json);
      
    } else {
      setErroresImportacion(resultado.errores);
      showToast(`✗ Error en la importación: ${resultado.errores[0]}`, "error");
    }


    if (resultado.erroresDetallados.length > 0 && resultado.errores.length > 0) {
      setErroresImportacion(resultado.errores);
    }
    
    setProcesando(false);
  };


  const handleLimpiarArchivo = () => {
    setArchivoSeleccionado(null);
    setUsuariosImportados([]);
    setErroresImportacion([]);
    setImportacionValida(false);
    setJsonGenerado(null);
    setDuplicados([]);
    setErroresDetallados([]);
    setPaginaActual(0);
  };

  const reset = () => {
    setDistribuidorSeleccionado(null)
    handleLimpiarArchivo();
    setEstadisticas({ filasValidas: 0, filasInvalidas: 0, filasVacias: 0, totalFilas: 0 });
    setDuplicados([]);
    setNumeroMeses("12");
    setNumeroActivaciones("1");
  }

  const [openCursosDialog, setOpenCursosDialog] = useState(false);
  const [tempCursosSeleccionados, setTempCursosSeleccionados] = useState<any[]>([]);
  const [cursosSeleccionados, setCursosSeleccionados] = useState<any[]>([]);

  const handleOpenCursosDialog = () => {
    setTempCursosSeleccionados([...cursosSeleccionados]);
    setOpenCursosDialog(true);
  };

  const handleCloseCursosDialog = () => {
    setOpenCursosDialog(false);
  };

  const handleSaveCursos = () => {
    setCursosSeleccionados([...tempCursosSeleccionados]);
    setOpenCursosDialog(false);
  };

  const handleAddCurso = (curso: { code: string; name: string }) => {
    if (!tempCursosSeleccionados.find((c: any) => c.code === curso.code)) {
      setTempCursosSeleccionados((prev: any) => [
        ...prev,
        { ...curso, chatEnabled: false },
      ]);
    }
  };

  const handleRemoveCurso = (cursoCode: string) => {
    setTempCursosSeleccionados((prev: any) =>
      prev.filter((c: any) => c.code !== cursoCode)
    );
  };

  const handleToggleChatInModal = (cursoCode: string) => {
    setTempCursosSeleccionados((prev: any) =>
      prev.map((curso: any) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      )
    );
  };

  const handleToggleChatMasivo = (enabled: boolean) => {
    setTempCursosSeleccionados((prev: any) =>
      prev.map((curso: any) => ({ ...curso, chatEnabled: enabled }))
    );
  };

  const cursosDisponiblesParaAgregar = cursosDisponibles.filter(
    (curso: any) => !tempCursosSeleccionados.find((c: any) => c.code === curso.code)
  );

  const getDistributors = async () => {
    const result = await HttpClient().get(`/admin/distributor`);
    if ((result as any)?.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de distribuidores. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setDistribuidoresDisponibles((result as any)?.data || []);
    setOpenLoader(false);
  };

  const getCourses = async () => {
    const result = await HttpClient().get(`/admin/courses`);
    if ((result as any)?.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de cursos. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setCursosDisponibles((result as any)?.data || []);
    setOpenLoader(false);
  };

  const sendData = async (data: any) => {
    setOpenLoader(true);
    const result = await HttpClient().post(`/admin/importer-user/create`, data);
    if ((result as any)?.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo guardar el lote. Intenta nuevamente.",
        "error"
      );
      return;
    }
    setOpenLoader(false);
    if ((result as any)?.status == 201) {
      showToast(
        "El lote fue guardado correctamente.",
        "success"
      );
      reset();
    } else {
      showToast(
        "No se creó el lote. Intenta nuevamente. Si el error persiste contacta al soporte.",
        "warning"
      );
    }
  };

  const handleGenerar = async() => {
    if (!distribuidorSeleccionado || !jsonGenerado) {
      showToast(
        "Por favor, selecciona un distribuidor y carga usuarios", "error"
      );
      return;
    }
    const cursosUnicosSet = new Set<string>();
    const selectedCoursesData: Array<{code: string, name: string, chatEnabled: boolean}> = [];

    jsonGenerado.usuarios.forEach((usuario: UsuarioImportado) => {
      if (usuario.cursos) {
        usuario.cursos.split(",").forEach((curso: string) => {
          const cursoCode = curso.trim();
          if (cursoCode && !cursosUnicosSet.has(cursoCode)) {
            cursosUnicosSet.add(cursoCode);
            const cursoDatos = cursosDisponibles.find((c: any) => c.code === cursoCode);
            const tieneChatEnAlguienUser = jsonGenerado.usuarios.some((u: UsuarioImportado) => {
              return u.cursos_chat && u.cursos_chat.split(",").some((c: string) => c.trim() === cursoCode);
            });
            
            selectedCoursesData.push({
              code: cursoCode,
              name: cursoDatos?.name || cursoCode,
              chatEnabled: tieneChatEnAlguienUser
            });
          }
        });
      }
    });

    const payload = {
      distributor: distribuidorSeleccionado.id,
      usuarios: jsonGenerado.usuarios,
      months: parseInt(numeroMeses) || 12,
      activations: parseInt(numeroActivaciones) || 1,
      selectedCoursesData: selectedCoursesData,
    };
    console.log(payload)

    await sendData(payload);
  };

  const isFormValid = () => {
    return (
      distribuidorSeleccionado !== null &&
      jsonGenerado !== null
    );
  };

  const descargarPlantillaExcel = () => {
    // Crear datos de ejemplo
    const datosEjemplo = [
      {
        nombre_completo: "Juan Pérez García",
        documento: "1234567890",
        correo: "juan.perez@ejemplo.com",
        nickname: "juan.perez",
        fecha_nacimiento: "1990-05-15",
        password: "Contraseña123!",
        whatsapp: "+573001234567",
        cursos: "ING1,EX19",
        cursos_chat: "ING1"
      }
    ];

    // Crear hoja de datos
    const ws = XLSX.utils.json_to_sheet(datosEjemplo);
    
    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 20 },  // nombre_completo
      { wch: 15 },  // documento
      { wch: 25 },  // email
      { wch: 15 },  // nickname
      { wch: 15 },  // fecha_nacimiento
      { wch: 20 },  // password
      { wch: 18 },  // whatsapp
      { wch: 20 },  // cursos
      { wch: 15 }   // cursos_chat
    ];

    // Crear otra hoja con instrucciones
    const instructionData = [
      { "Columna": "nombre_completo", "Descripción": "Nombre completo del usuario", "Requerido": "Sí", "Ejemplo": "Juan Pérez García" },
      { "Columna": "documento", "Descripción": "Documento de identidad", "Requerido": "Sí", "Ejemplo": "1234567890" },
      { "Columna": "correo", "Descripción": "Correo electrónico único", "Requerido": "Sí", "Ejemplo": "juan@ejemplo.com" },
      { "Columna": "nickname", "Descripción": "Nombre de usuario (a-z, 0-9, ., -, _) máx 64 caracteres", "Requerido": "Sí", "Ejemplo": "juan.perez" },
      { "Columna": "fecha_nacimiento", "Descripción": "Fecha en formato YYYY-MM-DD", "Requerido": "Sí", "Ejemplo": "1990-05-15" },
      { "Columna": "password", "Descripción": "Contraseña", "Requerido": "Sí", "Ejemplo": "Contraseña123!" },
      { "Columna": "whatsapp", "Descripción": "Teléfono con código país (+57 para Colombia)", "Requerido": "Sí", "Ejemplo": "+573001234567" },
      { "Columna": "cursos", "Descripción": "Códigos de cursos separados por comas", "Requerido": "Sí", "Ejemplo": "ING1,EX19,WO19" },
      { "Columna": "cursos_chat", "Descripción": "Curso para chat (debe estar en 'cursos')", "Requerido": "No", "Ejemplo": "ING1" }
    ];

    const wsInstructions = XLSX.utils.json_to_sheet(instructionData);
    wsInstructions['!cols'] = [
      { wch: 18 },
      { wch: 35 },
      { wch: 12 },
      { wch: 25 }
    ];

    // Crear workbook con ambas hojas
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ejemplo");
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instrucciones");

    // Descargar
    XLSX.writeFile(wb, "plantilla_importacion_usuarios.xlsx");
    showToast("Plantilla descargada exitosamente", "success");
  };


  useEffect(() => {
    getDistributors();
    getCourses();
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      {/* Header */}
      <OverlayLoader open={openLoader} ready={false} buttonClick={() => {}} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#374151"
          sx={{ fontSize: "1.3rem" }}
        >
          Gestor de Usuarios y Seriales
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Sección de Importación de Usuarios */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              backgroundColor: "white",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CloudUpload sx={{ color: "#3B82F6", fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Importar Usuarios desde Excel
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={descargarPlantillaExcel}
                  size="small"
                  startIcon={<Download />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Descargar Plantilla
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  <strong>¿Necesitas ayuda?</strong> Haz clic en "Descargar Plantilla" para obtener un archivo Excel con ejemplos e instrucciones sobre la estructura requerida.
                </Typography>
              </Alert>

              {/* Input File con estilo MUI */}
              <Paper
                sx={{
                  p: 3,
                  border: "2px dashed #3B82F6",
                  backgroundColor: dragActive ? "#E0F2FE" : "#F0F9FF",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#E0F2FE",
                    borderColor: "#0284C7",
                  },
                  mb: 2,
                  position: "relative",
                  ...(dragActive && {
                    backgroundColor: "#BFDBFE",
                    borderColor: "#0284C7",
                    borderWidth: "2px",
                  }),
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {procesando && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: 1,
                      zIndex: 10,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress sx={{ mb: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Procesando archivo...
                      </Typography>
                    </Box>
                  </Box>
                )}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  style={{
                    display: "none",
                  }}
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  style={{
                    display: "block",
                    cursor: "pointer",
                    padding: "1rem",
                  }}
                >
                  <CloudUpload
                    sx={{
                      fontSize: 48,
                      color: "#3B82F6",
                      mb: 1,
                    }}
                  />
                  <Typography variant="body1" fontWeight="bold" color="#1E40AF">
                    Haz clic o arrastra un archivo Excel
                  </Typography>
                  <Typography variant="caption" color="#64748B">
                    Máximo 10MB • Máximo 1000 filas
                  </Typography>
                </label>
              </Paper>
              <Divider sx={{ my: 2 }} />

              {/* Cursos */}
              <Box sx={{ mb: 3 }}>

                <Box
                  sx={{ mt: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add sx={{ fontSize: 14 }} />}
                    onClick={handleOpenCursosDialog}
                    sx={{
                      backgroundColor: "#10B981",
                      "&:hover": { backgroundColor: "#059669" },
                      borderRadius: 1.5,
                      fontSize: "0.75rem",
                    }}
                  >
                    Ver códigos de Cursos
                  </Button>

                </Box>
              </Box>

              {/* Archivo seleccionado */}
              {archivoSeleccionado && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    backgroundColor: "#F1F5F9",
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {importacionValida ? (
                      <CheckCircle sx={{ color: "#10B981", fontSize: 24 }} />
                    ) : (
                      <CloudUpload sx={{ color: "#6B7280", fontSize: 24 }} />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {archivoSeleccionado.name}
                      </Typography>
                      <Typography variant="caption" color="#6B7280">
                        {(archivoSeleccionado.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={handleLimpiarArchivo}
                    sx={{
                      color: "#EF4444",
                      "&:hover": { backgroundColor: "#FEE2E2" },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              )}

              {/* Errores de importación */}
              {erroresImportacion.length > 0 && (
                <>
                  {/* Mostrar datos detallados de errores PRIMERO */}
                  {erroresDetallados.length > 0 && (
                    <Box sx={{ mb: 3, border: "2px solid #DC2626", borderRadius: 2, p: 2.5, backgroundColor: "#FEF2F2" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: "#991B1B", fontSize: "1rem" }}>
                          📋 Detalles de filas con errores ({erroresDetallados.length})
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "500px", overflowY: "auto" }}>
                        {erroresDetallados.slice(0, 15).map((error, idx) => (
                          <Box key={idx} sx={{ 
                            p: 1.5, 
                            backgroundColor: "white", 
                            borderRadius: 1, 
                            border: "1px solid #FECACA",
                            borderLeft: "3px solid #DC2626"
                          }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5, color: "#991B1B" }}>
                              Fila {error.fila}
                            </Typography>
                            
                            {/* Mostrar datos válidos e inválidos */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
                              {error.nombre && error.nombre !== "(vacío)" && (
                                <Box sx={{ p: 0.75, backgroundColor: "#DCFCE7", borderRadius: 0.5, border: "1px solid #86EFAC" }}>
                                  <Typography variant="caption" sx={{ color: "#15803D", display: "block", fontSize: "0.7rem" }}>
                                    <strong>Nombre:</strong> {error.nombre}
                                  </Typography>
                                </Box>
                              )}
                              {error.documento && error.documento !== "(vacío)" && (
                                <Box sx={{ p: 0.75, backgroundColor: "#DCFCE7", borderRadius: 0.5, border: "1px solid #86EFAC" }}>
                                  <Typography variant="caption" sx={{ color: "#15803D", display: "block", fontSize: "0.7rem" }}>
                                    <strong>Documento:</strong> {error.documento}
                                  </Typography>
                                </Box>
                              )}
                              {error.correo && (
                                <Box sx={{ 
                                  p: 0.75, 
                                  backgroundColor: error.correo === "(vacío)" ? "#FEE2E2" : "#DCFCE7", 
                                  borderRadius: 0.5,
                                  border: error.correo === "(vacío)" ? "1px solid #FECACA" : "1px solid #86EFAC"
                                }}>
                                  <Typography variant="caption" sx={{ 
                                    color: error.correo === "(vacío)" ? "#B91C1C" : "#15803D", 
                                    display: "block",
                                    fontSize: "0.7rem"
                                  }}>
                                    <strong>Correo:</strong> {error.correo}
                                  </Typography>
                                </Box>
                              )}
                              {error.fecha_nacimiento && (
                                <Box sx={{ 
                                  p: 0.75, 
                                  backgroundColor: error.fecha_nacimiento === "(vacío)" ? "#FEE2E2" : "#DCFCE7", 
                                  borderRadius: 0.5,
                                  border: error.fecha_nacimiento === "(vacío)" ? "1px solid #FECACA" : "1px solid #86EFAC"
                                }}>
                                  <Typography variant="caption" sx={{ 
                                    color: error.fecha_nacimiento === "(vacío)" ? "#B91C1C" : "#15803D", 
                                    display: "block",
                                    fontSize: "0.7rem"
                                  }}>
                                    <strong>Fecha Nac.:</strong> {error.fecha_nacimiento}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Mostrar problemas */}
                            <Box sx={{ pt: 0.75, borderTop: "1px solid #FECACA", backgroundColor: "#FECACA20", p: 0.75, borderRadius: 0.5 }}>
                              <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#991B1B", fontWeight: "bold", fontSize: "0.75rem" }}>
                                ⚠️ DEBE CORREGIR:
                              </Typography>
                              <Box component="ul" sx={{ pl: 1.5, my: 0.25, margin: 0 }}>
                                {error.problemas.map((problema, pIdx) => (
                                  <li key={pIdx} style={{ marginBottom: "2px" }}>
                                    <Typography variant="caption" sx={{ color: "#991B1B", fontSize: "0.7rem" }}>
                                      {problema}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                        {erroresDetallados.length > 15 && (
                          <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#991B1B", fontWeight: "bold", textAlign: "center" }}>
                            ... y {erroresDetallados.length - 15} filas más con errores
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Alert resumen de errores */}
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                      Resumen de errores ({erroresImportacion.length}):
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, my: 1 }}>
                      {erroresImportacion.slice(0, 5).map((error, idx) => (
                        <li key={idx}>
                          <Typography variant="caption">{error}</Typography>
                        </li>
                      ))}
                      {erroresImportacion.length > 5 && (
                        <li>
                          <Typography variant="caption" fontWeight="bold">
                            ... y {erroresImportacion.length - 5} errores más
                          </Typography>
                        </li>
                      )}
                    </Box>
                  </Alert>
                </>
              )}

              {/* Resumen de estadísticas */}
              {archivoSeleccionado && (
                <Alert 
                  severity={importacionValida ? "success" : "warning"} 
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="caption" display="block">
                        Total:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {estadisticas.totalFilas}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="caption" display="block">
                        Válidas:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#10B981" }}>
                        {estadisticas.filasValidas}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="caption" display="block">
                        Inválidas:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#F59E0B" }}>
                        {estadisticas.filasInvalidas}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="caption" display="block">
                        Vacías:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#6B7280" }}>
                        {estadisticas.filasVacias}
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              )}

              {/* Alerta de duplicados encontrados */}
              {duplicados.length > 0 && (
                <Alert 
                  severity="info"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Registros Duplicados: {duplicados.length}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {duplicados.map((dup, idx) => (
                      <Typography key={idx} variant="caption" display="block" sx={{ mb: 0.5 }}>
                        • Fila {dup.fila}: {dup.tipo === "documento" ? "Documento" : "Correo"} duplicado ({dup.valor}) - Se mantuvo el primer registro
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              {/* Resumen de usuarios importados */}

              {/* Vista previa de datos */}
              {usuariosImportados.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Vista Previa ({usuariosImportados.length} usuarios totales):
                    </Typography>
                    <Typography variant="caption" color="#6B7280">
                      Página {paginaActual + 1} de {Math.ceil(usuariosImportados.length / filasPerPagina)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      overflowX: "auto",
                      overflowY: "auto",
                      border: "1px solid #E5E7EB",
                      borderRadius: 1,
                      height: "350px",
                      display: "block",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                        height: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#F3F4F6",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#D1D5DB",
                        borderRadius: "3px",
                        "&:hover": {
                          backgroundColor: "#9CA3AF",
                        },
                      },
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead style={{ position: "sticky", top: 0 }}>
                        <tr style={{ backgroundColor: "#F3F4F6" }}>
                          {COLUMNAS_REQUERIDAS.map((col) => (
                            <th
                              key={col}
                              style={{
                                padding: "4px 6px",
                                textAlign: "left",
                                fontSize: "10px",
                                fontWeight: "bold",
                                borderBottom: "1px solid #E5E7EB",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {usuariosImportados
                          .slice(paginaActual * filasPerPagina, (paginaActual + 1) * filasPerPagina)
                          .map((usuario, idx) => (
                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#FAFAFA" : "white" }}>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.nombre_completo}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.documento}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.fecha_nacimiento}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.password}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.whatsapp}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.correo}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.nickname}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.cursos}
                              </td>
                              <td style={{ padding: "8px 6px", fontSize: "11px", borderBottom: "1px solid #E5E7EB" }}>
                                {usuario.cursos_chat}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </Box>
                  {Math.ceil(usuariosImportados.length / filasPerPagina) > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                      <Pagination
                        count={Math.ceil(usuariosImportados.length / filasPerPagina)}
                        page={paginaActual + 1}
                        onChange={(_, page) => setPaginaActual(page - 1)}
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              )}

            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              backgroundColor: "white",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Distribuidor */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
                  >
                    <Business sx={{ color: "#6B7280", fontSize: 16 }} />
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Distribuidor:
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <CustomAutocomplete 
                        options={distribuidoresDisponibles as any}
                        value={distribuidorSeleccionado}
                        onChange={(event: any, newValue: any) => {
                          setDistribuidorSeleccionado(newValue);
                        }}
                    />
                    <Avatar
                      sx={{ width: 28, height: 28, backgroundColor: "#6B7280" }}
                    >
                      <Business sx={{ fontSize: 14 }} />
                    </Avatar>
                  </Box>
                </Grid>
              </Grid>

              {/* Meses y Activaciones */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: "bold", fontSize: "0.85rem" }}>
                    Número de Meses:
                  </Typography>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={numeroMeses}
                    onChange={(e) => setNumeroMeses(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: "bold", fontSize: "0.85rem" }}>
                    Número de Activaciones (no modificable):
                  </Typography>
                  <input
                  readOnly
                    type="number"
                    min="1"
                    max="999"
                    value={numeroActivaciones}
                    onChange={(e) => setNumeroActivaciones(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Botón Generar */}
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleGenerar}
                  disabled={!isFormValid()}
                  sx={{
                    backgroundColor: "#9CA3AF",
                    color: "white",
                    fontWeight: "bold",
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    "&:hover": {
                      backgroundColor: "#6B7280",
                    },
                    "&:disabled": {
                      backgroundColor: "#D1D5DB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  Generar
                </Button>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CourseManagerDialog
        open={openCursosDialog}
        onClose={handleCloseCursosDialog}
        onSave={handleSaveCursos}
        selectedCourses={tempCursosSeleccionados as any}
        availableCourses={cursosDisponiblesParaAgregar as any}
        onToggleChatAll={handleToggleChatMasivo}
        onToggleChat={handleToggleChatInModal}
        onRemoveCourse={handleRemoveCurso}
        onAddCourse={handleAddCurso}
        onlyView={true}
      />
    </Box>
  );
}
